import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDeliveryAreas } from "@/hooks/useDeliveryAreas";
import { ManualCustomerStep, type ManualCustomer } from "@/components/admin/ManualCustomerStep";
import { ManualOrderBuilder } from "@/components/admin/ManualOrderBuilder";
import { istToday } from "@/lib/admin/production.schemas";

export const Route = createFileRoute("/_admin/admin/manual-orders")({
  component: ManualOrdersPage,
});

const EMPTY: ManualCustomer = {
  customerId: null,
  fullName: "",
  phone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  pincode: "",
};

function ManualOrdersPage() {
  const { areas, isLoading } = useDeliveryAreas();
  const [areaId, setAreaId] = useState("");
  const [onDate, setOnDate] = useState(istToday());
  const [customer, setCustomer] = useState<ManualCustomer>(EMPTY);
  const [confirmed, setConfirmed] = useState(false);
  const [lastRef, setLastRef] = useState<string | null>(null);

  const areaName = areas.find((a) => a.id === areaId)?.neighbourhood ?? "";
  const ready = confirmed && Boolean(areaId);

  const reset = () => {
    setCustomer(EMPTY);
    setConfirmed(false);
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Manual Orders"
        description="Create phone, walk-in, WhatsApp or corporate orders using the same ordering engine as the website."
      />

      {lastRef ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
          <span>
            Order <strong>{lastRef}</strong> created and queued for the kitchen.
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to="/admin/orders">Open Orders</Link>
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setLastRef(null);
                reset();
              }}
            >
              New manual order
            </Button>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3 rounded-lg border border-border p-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="mo-area">Delivery area</Label>
          <Select value={areaId} onValueChange={setAreaId} disabled={isLoading}>
            <SelectTrigger id="mo-area">
              <SelectValue placeholder={isLoading ? "Loading…" : "Select an area"} />
            </SelectTrigger>
            <SelectContent>
              {areas.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.neighbourhood}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="mo-date">Delivery date</Label>
          <Input
            id="mo-date"
            type="date"
            value={onDate}
            onChange={(e) => setOnDate(e.target.value || istToday())}
          />
        </div>
      </div>

      <ManualCustomerStep
        value={customer}
        onChange={(next) => {
          setCustomer(next);
          setConfirmed(false);
        }}
        onConfirm={() => setConfirmed(true)}
        confirmed={confirmed}
      />

      {ready && !lastRef ? (
        <ManualOrderBuilder
          areaId={areaId}
          areaName={areaName}
          onDate={onDate}
          customer={customer}
          onSaved={setLastRef}
        />
      ) : !lastRef ? (
        <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
          Select a delivery area and confirm the customer to start building the order.
        </p>
      ) : null}
    </div>
  );
}
